import { Transform, TransformPosition, TransformRotation } from '@ikea-aoa/elements-3d';
import { TransformVector } from '../utils/TransformVector';
import { Groups, ConnectionRelation, ProductData, Model } from '.';
import { FamilyType, ModelData } from './types';
import { SceneObject } from '..';

export interface ConnectableObject {
	id: string;
	articleId: string;
	parentId: string | null;
	transform: Transform;
	addedWithClick?: boolean;
}

export interface ConnectedObjects {
	parentId: string;
	childId: string;
	relation: ConnectionRelation;
}

export interface ConnectionFilter {
	valid: boolean;
	available: boolean;
}

/**
 * Connection Manager.
 */
export class ConnectionManager {
	constructor(
		private sceneObjects: Map<string, ConnectableObject>,
		private productData: ProductData,
		private groups: Groups,
		private models: ModelData
	) {}
	private connections = new Array<ConnectedObjects>();
	private allConnections = new Array<ConnectedObjects>();

	public setRules(productData: ProductData, groups: Groups, models: ModelData): void {
		this.productData = productData;
		this.groups = groups;
		this.models = models;
	}

	public add(
		child: ConnectableObject,
		parent: ConnectableObject,
		relation: ConnectionRelation
	): void {
		this.allConnections.push({
			childId: child.id,
			parentId: parent.id,
			relation,
		});
	}

	public remove(sceneObject: ConnectableObject): void {
		const lockId = this.allConnections.findIndex(
			(conn) =>
				(conn.childId === sceneObject.id && conn.parentId === sceneObject.parentId) ||
				conn.childId === sceneObject.id ||
				(sceneObject.parentId === null && conn.parentId === sceneObject.id)
		);
		if (lockId !== -1) {
			this.allConnections.splice(lockId, 1);
		}
	}

	public connect(
		child: ConnectableObject,
		parent: ConnectableObject,
		relation: ConnectionRelation
	): void {
		const transform = this.getConnectedTransform(child.articleId, parent, relation);
		const connection: ConnectedObjects = {
			childId: child.id,
			parentId: parent.id,
			relation,
		};

		this.connections.push(connection);
		child.parentId = parent.id;
		child.transform.position = transform.position;
		child.transform.rotation = transform.rotation;
	}

	public disconnect(
		sceneObject: ConnectableObject,
		position?: TransformPosition,
		rotation?: TransformRotation
	): void {
		const lockId = this.connections.findIndex((conn) => conn.childId === sceneObject.id);
		if (lockId > -1) {
			const pos = position ? position : this.getWorldPosition(sceneObject);
			const rot = rotation ? rotation : this.getWorldRotation(sceneObject);
			this.connections.splice(lockId, 1);
			sceneObject.parentId = null;
			sceneObject.transform.position = pos;
			sceneObject.transform.rotation = rot;
		}
	}

	public getConnectedTransform(
		childArticleId: string,
		parentArticleId: string,
		relation: ConnectionRelation
	): Transform;

	public getConnectedTransform(
		childArticleId: string,
		parent: ConnectableObject,
		relation: ConnectionRelation
	): Transform;

	public getConnectedTransform(
		childArticleId: string,
		parent: ConnectableObject | string,
		relation: ConnectionRelation
	): Transform {
		// validating overload
		let parentArticleId: string;
		if (typeof parent == 'object') {
			parentArticleId = parent.articleId;
		} else {
			parentArticleId = parent;
		}
		const rotation = TransformVector.zero();
		if (relation.rotation) {
			rotation.x = relation.rotation.x ? relation.rotation.x : 0;
			rotation.y = relation.rotation.y ? relation.rotation.y : 0;
			rotation.z = relation.rotation.z ? relation.rotation.z : 0;
		}
		const childModel = this.getModelData(childArticleId);
		const parentModel = this.getModelData(parentArticleId);
		if (childModel && parentModel) {
			const childPointPosition = childModel.connections[relation.child].position;
			const parentPointPosition = parentModel.connections[relation.parent].position;
			const offset = TransformVector.rotate(childPointPosition, rotation);
			const newPosition = TransformVector.add(
				TransformVector.sub(parentPointPosition, offset),
				relation.position as TransformPosition
			);

			return {
				position: newPosition,
				rotation: rotation,
			} as Transform;
		} else {
			throw new Error('Not able to get ModelData');
		}
	}

	public getConnectedWorldTransform(
		childArticleId: string,
		parent: ConnectableObject,
		relation: ConnectionRelation
	): Transform;

	public getConnectedWorldTransform(
		childArticleId: string,
		parentArticleId: string,
		relation: ConnectionRelation,
		parentWorldTransform: Transform
	): Transform;

	public getConnectedWorldTransform(
		childArticleId: string,
		parent: string | ConnectableObject,
		relation: ConnectionRelation,
		parentWorldTransform?: Transform
	): Transform {
		const localTransform =
			typeof parent == 'string'
				? this.getConnectedTransform(childArticleId, parent as string, relation)
				: this.getConnectedTransform(childArticleId, parent as ConnectableObject, relation);
		return typeof parent == 'string'
			? this.getRelativeWorldTransform(localTransform, parentWorldTransform as Transform)
			: this.getRelativeWorldTransformFromObject(localTransform, parent);
	}

	public getAllRelations(
		child: ConnectableObject,
		filter: ConnectionFilter,
		multipleSofas?: boolean,
		ignoreAutoConnected?: boolean,
		checkAllConnections?: boolean
	): Map<string, ConnectionRelation[]>;

	public getAllRelations(
		articleId: string,
		filter: ConnectionFilter,
		multipleSofas?: boolean,
		ignoreAutoConnected?: boolean,
		checkAllConnections?: boolean
	): Map<string, ConnectionRelation[]>;

	public getAllRelations(
		child: ConnectableObject | string,
		filter: ConnectionFilter,
		multipleSofas = true,
		ignoreAutoConnected = true,
		checkAllConnections = false
	): Map<string, ConnectionRelation[]> {
		let childObject: ConnectableObject | null = null;
		let childModel: Model | null = null;

		// validating overload
		if (typeof child == 'object') {
			childObject = child;
			const model = this.getModelData(child.articleId);
			childModel = model ? model : null;
		} else if (typeof child == 'string') {
			childObject = null;
			const model = this.getModelData(child);
			childModel = model ? model : null;
		}

		const validRelations = new Map<string, Array<ConnectionRelation>>();
		if (childModel) {
			this.sceneObjects.forEach((parentObject) => {
				if (childObject) {
					// Can not connect to itself
					if (childObject.id == parentObject.id) {
						return;
					}
					// Can not connect to its own children
					const children = this.getChildren(childObject.id);
					if (children.includes(parentObject.id)) {
						// Allow connection to notChildOnly children for modular sofas
						const canBeStandalone = multipleSofas && !this.isChildOnly(parentObject.articleId);
						if (!canBeStandalone) {
							return;
						}
					}
				}
				const parentModel = this.getModelData(parentObject.articleId);
				if (parentModel) {
					let relations = this.getRelations(childModel as Model, parentModel);
					if (relations.length && (filter.valid || filter.available || !multipleSofas)) {
						relations = relations.filter((relation) => {
							// Check if relation is valid
							const valid = filter.valid ? this.isValid(relation) : true;
							if (!valid) {
								return false;
							}
							// Check if connection-points are not occupied
							const available = filter.available
								? !this.isOccupided(
										childObject ? childObject.id : null,
										parentObject.id,
										relation,
										childModel?.logic?.checkAutoConnections ? false : ignoreAutoConnected,
										checkAllConnections
								  )
								: true;
							if (!available) {
								return false;
							}
							// Check creating multiple sofas
							const isAllowed = !multipleSofas
								? !this.isNewStandaloneSofa(
										childObject && this.sceneObjects.has(childObject.id) ? childObject : null,
										parentObject
								  )
								: true;
							if (!isAllowed) {
								return false;
							}
							return true;
						});
					}
					if (relations.length) {
						validRelations.set(parentObject.id, relations);
					}
				}
			});
		}

		return validRelations;
	}

	/**
	 * Gets connection relations between two models.
	 */
	public getRelations(child: Model, parent: Model): Array<ConnectionRelation> {
		const validRelations = new Array<ConnectionRelation>();
		Object.entries(parent.relations).forEach(([id, relations]) => {
			if (this.groups[id] && this.groups[id].models.includes(child.modelId)) {
				relations.forEach((rel) => {
					if (child.connections[rel.child] && parent.connections[rel.parent]) {
						validRelations.push(rel);
					}
				});
			}
		});
		return validRelations;
	}

	/**
	 * Returns all connections.
	 */
	public getAllConnections(): ConnectedObjects[] {
		return this.connections;
	}

	/**
	 * Get existing connection between two objects.
	 */
	public getConnection(childId: string, parentId: string): ConnectionRelation | null {
		const con = this.connections.find((c) => c.childId == childId && c.parentId == parentId);
		return con ? con.relation : null;
	}

	/**
	 * Clear all connections.
	 */
	public clearConnections(): void {
		this.connections = new Array<ConnectedObjects>();
		this.allConnections = new Array<ConnectedObjects>();
	}

	/**
	 * Check if connection is not occupied.
	 */
	public isOccupided(
		childId: string | null,
		parentId: string,
		relation: ConnectionRelation,
		ignoreAutoConnected?: boolean,
		checkAllConnections?: boolean
	): boolean {
		const connections = checkAllConnections ? this.allConnections : this.connections;
		return connections.some((conn) => {
			if (ignoreAutoConnected && this.isAutoConnectedObjects(conn)) {
				return false;
			}
			return (
				(conn.parentId === parentId && relation.parent === conn.relation.parent) ||
				(conn.childId === parentId && relation.parent === conn.relation.child) ||
				(conn.parentId === childId && relation.child === conn.relation.parent)
			);
		});
	}

	public connectionIsOccupied(itemId: string, connectionName: string): boolean {
		return this.connections.some((conn) => {
			return (
				(conn.parentId === itemId && connectionName === conn.relation.parent) ||
				(conn.childId === itemId && connectionName === conn.relation.child)
			);
		});
	}

	public getOccupiedConnections(itemId: string, productId: string): string[] {
		const occupiedConnections: string[] = [];
		const modelData = this.getModelData(productId);

		if (modelData) {
			Object.keys(modelData.connections).forEach((connection) => {
				if (this.connectionIsOccupied(itemId, connection)) {
					occupiedConnections.push(connection);
				}
			});
		}

		return occupiedConnections;
	}

	/**
	 * Validate connection relation.
	 */
	public isValid(relation: ConnectionRelation): boolean {
		return relation.type !== 'INVALID';
	}

	public isNewStandaloneSofa(child: ConnectableObject | null, parent: ConnectableObject): boolean {
		const l = child ? 1 : 0;
		const children = this.getChildren(parent.id);
		if (this.isSeparate(parent.articleId) && !parent.parentId && !children.length) {
			const standalones = this.getAllStandaloneObjects();
			if (standalones.length > l) {
				if (child) {
					if (standalones.every((s) => s.id == child.id)) {
						return false;
					}
				}
				return true;
			}
		}
		return false;
	}

	public isSeparate(articleId: string): boolean {
		const model = this.getModelData(articleId);
		if (model && model.logic && model.logic.separate) {
			return true;
		}
		return false;
	}

	public isAddAsChild(articleId: string): boolean {
		const model = this.getModelData(articleId);
		if (model && model.logic && model.logic.addAsChild) {
			return true;
		}
		return false;
	}

	public isChildOnly(articleId: string): boolean {
		const model = this.getModelData(articleId);
		if (model && model.logic && model.logic.addAsChild && !model.logic.separate) {
			return true;
		}
		return false;
	}

	// TODO rename to something like notAsAsChild objects?
	public getAllStandaloneObjects(): Array<ConnectableObject> {
		const objects = new Array<ConnectableObject>();
		this.sceneObjects.forEach((item) => {
			if (!this.isAddAsChild(item.articleId)) {
				objects.push(item);
			}
		});
		return objects;
	}

	public getChildStandaloneObjects(parent: ConnectableObject): Array<ConnectableObject> {
		const objects = new Array<ConnectableObject>();
		const children = this.getChildren(parent.id);
		children.forEach((childId) => {
			const item = this.sceneObjects.get(childId);
			if (item && this.isStandalone(item)) {
				objects.push(item);
			}
		});
		return objects;
	}

	public getStandaloneParent(target: ConnectableObject): ConnectableObject {
		const isAddAsChild = this.isAddAsChild(target.articleId);
		if (target.parentId && isAddAsChild) {
			const parent = this.sceneObjects.get(target.parentId);
			if (parent) {
				return this.getStandaloneParent(parent);
			} else {
				throw 'ConnectionManager: getStandaloneParent(): No parent.';
			}
		}
		return target;
	}

	public getChildNotStandaloneObjects(parent: ConnectableObject): Array<ConnectableObject> {
		const objects = new Array<ConnectableObject>();
		const children = this.getChildren(parent.id);
		children.forEach((childId) => {
			const item = this.sceneObjects.get(childId);
			if (item && this.isAddAsChild(item.articleId)) {
				objects.push(item);
			}
		});
		return objects;
	}

	public getNotStandaloneChildren(parent: ConnectableObject): Array<ConnectableObject> {
		const objects = new Array<ConnectableObject>();
		const children = this.getChildren(parent.id);
		children.forEach((childId) => {
			const item = this.sceneObjects.get(childId);
			if (item && this.isAddAsChild(item.articleId)) {
				objects.push(item);
			}
		});
		return objects;
	}

	public isStandalone(sceneObject: ConnectableObject): boolean {
		const { articleId, id } = sceneObject;
		if (this.isAddAsChild(articleId)) {
			if (this.isSeparate(articleId)) {
				const children = this.getChildren(id);
				if (
					children.length &&
					children.some((i) => {
						const sceneObject = this.sceneObjects.get(i);
						if (sceneObject) {
							return !this.isAddAsChild(sceneObject.articleId);
						}
						return false;
					})
				) {
					return true;
				}
			}
			return false;
		}
		return true;
	}

	/**
	 * Get directly connected object ids.
	 */
	public getDirectlyConnected(id: string): Array<string> {
		return this.connections
			.filter((c) => c.parentId == id || c.childId == id)
			.map((c) => c.childId);
	}

	/**
	 * Get child ids.
	 */
	public getChildren(id: string): Array<string> {
		return this.connections.filter((c) => c.parentId == id).map((c) => c.childId);
	}

	/**
	 * Has children.
	 */
	public hasChildren(id: string): boolean {
		return this.connections.some((c) => c.parentId == id);
	}

	/**
	 * Get child objects.
	 */
	public getChildObjects(parent: ConnectableObject): Array<ConnectableObject> {
		return this.connections
			.filter((c) => c.parentId == parent.id)
			.map((c) => this.sceneObjects.get(c.childId) as ConnectableObject);
	}

	/**
	 * Get all child ids.
	 */
	public getAllChildren(id: string, acc: Array<string> = []): Array<string> {
		const children = this.getChildren(id);
		if (children.length) {
			acc.push(...children);
			children.forEach((c) => this.getAllChildren(c, acc));
		}
		return acc;
	}

	/**
	 * Get all child not standalone ids.
	 */
	public getAllChildOnlyChildren(id: string, acc: Array<string> = []): Array<string> {
		const children = this.getChildren(id);
		children.forEach((child) => {
			const obj = this.sceneObjects.get(child);
			if (obj && this.isChildOnly(obj.articleId)) {
				acc.push(child);
				this.getAllChildren(child, acc);
			}
		});
		return acc;
	}

	/**
	 * Get all child objects.
	 */
	public getAllChildObjects(
		parent: ConnectableObject,
		acc: Array<ConnectableObject> = []
	): Array<ConnectableObject> {
		const children = this.getChildObjects(parent);
		if (children.length) {
			acc.push(...children);
			children.forEach((c) => this.getAllChildObjects(c, acc));
		}
		return acc;
	}

	public getPointWorldPosition(
		point: string,
		sceneObect: ConnectableObject
	): TransformPosition | null {
		const model = this.getModelData(sceneObect.articleId);
		if (model) {
			const position = model.connections[point].position || TransformVector.zero();
			const rotation = model.connections[point].rotation || TransformVector.zero();
			return this.getRelativeWorldTransformFromObject({ position, rotation }, sceneObect)
				.position as TransformPosition;
		}
		return null;
	}

	private getAllParents(
		child: ConnectableObject,
		sceneObects: Map<string, ConnectableObject>
	): Array<ConnectableObject> {
		let target = child;
		const parents = new Array<ConnectableObject>();
		while (target && target.parentId) {
			const sceneObject = sceneObects.get(target.parentId);
			if (sceneObject) {
				target = sceneObject;
				parents.push(target);
			}
		}
		return parents;
	}

	private isAutoConnected(
		childId: string | null,
		parentId: string,
		relation: ConnectionRelation
	): boolean {
		if (childId) {
			const childObject = this.sceneObjects.get(childId);
			return !!childObject && this.isAutoConnectableModel(childObject.articleId);
		}
		const connection = this.connections.find(
			(c) => c.parentId == parentId && c.relation.parent == relation.parent
		);
		if (connection) {
			const connectedChildObject = this.sceneObjects.get(connection.childId) as ConnectableObject;
			const articleId = connectedChildObject.articleId;
			return this.isAutoConnectableModel(articleId);
		} else {
			throw 'ConnectionManager: isAutoConnected(): connection does not exist.';
		}
	}

	public isAutoConnectableModel(articleId: string): boolean {
		const model = this.getModelData(articleId);
		if (model && model.logic) {
			return model.logic.autoConnect === true;
		}
		return false;
	}

	public isAutoConnectMovable(sceneObject: SceneObject): boolean {
		if (sceneObject?.parentId) {
			const parentObject = this.sceneObjects.get(sceneObject.parentId);
			if (parentObject?.articleId) {
				const parentModel = this.getModelData(parentObject.articleId);
				const model = this.getModelData(sceneObject.articleId);
				if (model && model.logic && parentModel && parentModel.logic) {
					const family = this.getFamily(sceneObject.articleId) || '';
					return (
						model.logic.autoConnect === true &&
						model.logic.autoConnectMovable === true &&
						parentModel.logic.autoConnectLimit !== undefined &&
						!!parentModel.logic.autoConnectLimit[family]
					);
				}
			}
		}
		return false;
	}

	private isAutoConnectedObjects(connectedObjects: ConnectedObjects): boolean {
		const { childId, parentId, relation } = connectedObjects;
		return this.isAutoConnected(childId, parentId, relation);
	}

	// Helper functions

	public getModelData = (productId: string): Model | false => {
		const product = Object.values(this.productData).find(
			(productData) => productData.productId === productId || productData.previousId === productId
		);
		if (product) {
			return this.models[product.modelId];
		}
		return false;
	};

	public getFamily = (productId: string): FamilyType | null => {
		const id = Object.keys(this.productData).find((product) => {
			const productData = this.productData[product];
			return productData.productId === productId || productData.previousId === productId;
		});

		if (id) {
			const data = this.productData[id];

			return data && data.filter && data.filter.family ? data.filter.family : null;
		}
		return null;
	};

	public getWorldTransform(sceneObject: ConnectableObject): Transform {
		if (!sceneObject.parentId) {
			return sceneObject.transform;
		}
		const parents = this.getAllParents(sceneObject, this.sceneObjects).reverse();
		let position = TransformVector.zero();
		let rotation = TransformVector.zero();
		parents.forEach((parent) => {
			if (parent && parent.transform && parent.transform.rotation) {
				const offset = TransformVector.rotate(
					parent.transform.position || TransformVector.zero(),
					rotation
				);
				position = TransformVector.add(position, offset);
				rotation = TransformVector.add(rotation, parent.transform.rotation);
			}
		});
		const sceneObjectRotation = sceneObject.transform.rotation || TransformVector.zero();
		const offset = TransformVector.rotate(
			sceneObject.transform.position || TransformVector.zero(),
			rotation
		);
		const worldPosition = TransformVector.add(position, offset);
		const worldRotation = TransformVector.add(rotation, sceneObjectRotation);
		return {
			position: worldPosition,
			rotation: worldRotation,
		};
	}

	public getWorldRotation(sceneObject: ConnectableObject): TransformRotation {
		if (!sceneObject.parentId) {
			return sceneObject.transform.rotation || TransformVector.zero();
		}
		const parents = this.getAllParents(sceneObject, this.sceneObjects).reverse();
		let rotation: TransformRotation = TransformVector.zero();
		parents.forEach((parent) => {
			rotation = TransformVector.add(rotation, parent.transform.rotation || TransformVector.zero());
		});

		return TransformVector.add(rotation, sceneObject.transform.rotation || TransformVector.zero());
	}

	public getWorldPosition(sceneObject: ConnectableObject): TransformPosition {
		if (!sceneObject.parentId) {
			return sceneObject.transform.position || TransformVector.zero();
		}
		const parents = this.getAllParents(sceneObject, this.sceneObjects).reverse();
		let position = TransformVector.zero();
		let rotation = TransformVector.zero();
		parents.forEach((parent) => {
			const offset = TransformVector.rotate(
				parent.transform.position || TransformVector.zero(),
				rotation
			);
			position = TransformVector.add(position, offset);
			rotation = TransformVector.add(rotation, parent.transform.rotation || TransformVector.zero());
		});
		const offset = TransformVector.rotate(
			sceneObject.transform.position || TransformVector.zero(),
			rotation
		);
		return TransformVector.add(position, offset);
	}

	public getRelativeWorldTransformFromObject(
		transform: Transform,
		parent: ConnectableObject
	): Transform {
		const parentWorldPosition = this.getWorldPosition(parent);
		const parentWorldRotation = this.getWorldRotation(parent);

		return this.getRelativeWorldTransform(transform, {
			position: parentWorldPosition,
			rotation: parentWorldRotation,
		});
	}

	public getRelativeWorldTransform(
		childTransform: Transform,
		parentTransform: Transform
	): Transform {
		const parentWorldPosition = parentTransform.position || TransformVector.zero();
		const parentWorldRotation = parentTransform.rotation || TransformVector.zero();

		const mod = TransformVector.rotate(
			childTransform.position || TransformVector.zero(),
			parentWorldRotation
		);
		const worldPosition = TransformVector.add(parentWorldPosition, mod);
		const worldRotation = TransformVector.add(
			parentWorldRotation,
			childTransform.rotation || TransformVector.zero()
		);
		return {
			position: worldPosition,
			rotation: worldRotation,
		};
	}

	public isWideArmrest(articleId: string | undefined): boolean {
		if (!articleId) {
			return false;
		}
		const modelData = this.getModelData(articleId);
		if (modelData) {
			return modelData.modelId.includes('WIDE_ARMREST');
		}
		return false;
	}
}
